import {
	AccessEventAction,
	AuthEventAction,
	DealEventCategory,
	DealEventSource,
	DealEventStatus,
} from '@dealstack/shared/lib/backendClient';
import {
	DEAL_EVENT_STATUSES_BY_CATEGORY,
	// The generated client types the category as a union; the shared map is keyed by the enum.
	DealEventCategory as DealEventCategoryEnum,
	tablesInCategory,
} from '@dealstack/shared/lib/common/business/dealEvent';
import { formatTableNameToLabel } from '@dealstack/frontend/shared/components/Audit/AuditLayout/utils.ts';
import { normalizeScreamingSnakeToUI } from '@dealstack/shared/lib/format/string';
import {
	DealEventLogTab,
	type Translate,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.types.ts';
import {
	formatAccessHeading,
	formatAuthHeading,
	formatCategoryLabel,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';
import { DEAL_EVENT_LOG_TAB_LABEL } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogTabs.utils.ts';
import { getStatusLabel } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogStatus.utils.ts';
import {
	DEAL_EVENT_LOG_TAB_CATEGORIES,
	PARTICIPANT_TYPE_DETAILS,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogQuery.utils.ts';

export interface DealEventFilterOption {
	value: string;
	label: string;
}

// The shared map is keyed by the business enums; the table speaks the generated unions, whose
// members carry the same strings. Widening to string lets the two meet without a cast.
const REACHABLE_BY_CATEGORY: Record<string, string[]> = DEAL_EVENT_STATUSES_BY_CATEGORY;

/** Every status the tab's categories can report; ALL_EVENTS spans the lot. */
const getTabStatuses = (tab: DealEventLogTab): DealEventStatus[] => {
	const categories = DEAL_EVENT_LOG_TAB_CATEGORIES[tab] ?? Object.values(DealEventCategory);
	const reachable = new Set(
		categories.flatMap((category) => REACHABLE_BY_CATEGORY[category] ?? [])
	);

	return Object.values(DealEventStatus).filter((status) => reachable.has(status));
};

export const getDealEventStatusOptions = (
	tab: DealEventLogTab,
	t: Translate
): DealEventFilterOption[] =>
	getTabStatuses(tab).map((value) => ({ value, label: getStatusLabel(value, t) }));

const tabOfCategory = (category: DealEventCategory): DealEventLogTab | undefined =>
	Object.values(DealEventLogTab).find((tab) =>
		DEAL_EVENT_LOG_TAB_CATEGORIES[tab]?.includes(category)
	);

/** A category is named after the tab that holds it; one with no tab of its own keeps its name. */
export const formatDealEventCategoryLabel = (category: DealEventCategory, t: Translate): string => {
	const tab = tabOfCategory(category);

	return tab ? t(DEAL_EVENT_LOG_TAB_LABEL[tab]) : formatCategoryLabel(category, t);
};

const FILTERABLE_CATEGORIES: DealEventCategory[] = [
	DealEventCategory.PARTICIPANT,
	DealEventCategory.CAPITAL,
	DealEventCategory.ACCESS,
	DealEventCategory.MESSAGE,
	DealEventCategory.ADMIN,
];

export const getDealEventCategoryOptions = (
	t: Translate,
	canReadTeamAndAccess: boolean
): DealEventFilterOption[] =>
	FILTERABLE_CATEGORIES.filter(
		(category) => canReadTeamAndAccess || category !== DealEventCategory.ACCESS
	).map((value) => ({ value, label: formatDealEventCategoryLabel(value, t) }));

/**
 * The Type column shows which record changed, so the filter offers the same: one option per
 * audited table of the category, read off the shared map rather than listed again here.
 */
const auditedTableOptions = (
	category: DealEventCategoryEnum,
	t: Translate
): DealEventFilterOption[] =>
	tablesInCategory(category).map((table) => ({
		value: table,
		label: formatTableNameToLabel(table, t),
	}));

export const getDealEventCapitalTypeOptions = (t: Translate): DealEventFilterOption[] =>
	auditedTableOptions(DealEventCategoryEnum.CAPITAL, t);

export const getDealEventParticipantTypeOptions = (t: Translate): DealEventFilterOption[] => [
	...Object.keys(PARTICIPANT_TYPE_DETAILS).map((source) => ({
		value: source,
		label: normalizeScreamingSnakeToUI(source),
	})),
	...auditedTableOptions(DealEventCategoryEnum.PARTICIPANT, t),
];

/** Access and auth rows share the tab, and the feed names their types the same way it does. */
export const getDealEventAccessTypeOptions = (t: Translate): DealEventFilterOption[] => [
	...Object.values(AccessEventAction).map((action) => ({
		value: `${DealEventSource.ACCESS}_${action}`,
		label: formatAccessHeading(action, t),
	})),
	...Object.values(AuthEventAction).map((action) => ({
		value: `${DealEventSource.AUTH}_${action}`,
		label: formatAuthHeading(action, t),
	})),
];

export const withSelectedOption = (
	options: DealEventFilterOption[],
	selected: DealEventFilterOption | undefined
): DealEventFilterOption[] =>
	!selected || options.some((option) => option.value === selected.value)
		? options
		: [selected, ...options];
